<template>
  <Page>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <div class="title-container">
            <div class="title">ผู้ใช้</div>
          </div>
          <div class="actions">
            <router-link
              to="/accounts/create"
              class="btn btn-primary">
              <i class="fas fa-user-plus icon"></i>
              สร้างผู้ใช้
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadUsersState.isLoading" />
      <div v-if="!loadUsersState.isLoading">
        <!-- render content -->
        <Section v-if="!loadUsersState.error">
          <template v-slot:section-filter>
            <input
              class="form-control form-control-search"
              placeholder="ค้นหาด้วยชื่อผู้ใช้..."
              :value="filter.q"
              @change="onSearch" />
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-detail">
                    <div class="list-item list-item-primary list-item-title list-item-user-info">
                      <div class="user-detail">ชื่อ/อีเมล</div>
                    </div>
                    <div class="list-item">บทบาท</div>
                    <div class="list-item list-item-status">สถานะ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div class="list-item-container no-underline"
                    v-for="(item, itemIndex) in users"
                    :key="item.id"
                    @click.stop="goToPageDetail(`/accounts/${item.id}`, itemIndex, $event)">
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary list-item-title list-item-user-info">
                        <div class="user-image-container">
                          <Logo :user="item" />
                        </div>
                        <div class="user-detail">
                          <div class="title">{{item.name}}</div>
                          <div class="subtitle">{{item.email}}</div>
                        </div>
                      </div>
                      <div class="list-item">
                        {{getRole(item.role)}}
                      </div>
                      <div class="list-item list-item-status">
                        <div class="badge badge-status"
                          :class="showStatusColor(item.status)">
                          <span v-if="item.status==='active'">ใช้งานได้</span>
                          <span v-if="item.status==='suspended'">ถูกระงับ</span>
                        </div>
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/accounts/${item.id}`">
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div
                class="pagination-container"
                v-if="isShowPagination()">
                <Pagination :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list --->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`/accounts/create`">
                สร้างผู้ใช้
              </router-link>
            </EmptyMessage>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบผู้ใช้ที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch">
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <ErrorMessageUserContainer
          v-if="loadUsersState.error"
          :error="loadUsersState.error"/>
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import Logo from '@/components/Logo.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import Pagination from '@/components/Pagination.vue';

import { loadUsers } from './api';
import ErrorMessageUserContainer from './ErrorMessageUserContainer.vue';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    PreloaderSection,
    Section,
    List,
    Logo,
    EmptyMessage,
    Pagination,
    ErrorMessageUserContainer,
  },
  data() {
    return {
      users: null,
      lastPage: null,
      loadUsersState: {
        isLoading: true,
        error: null,
      },
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
  },
  methods: {
    async load(filter) {
      try {
        this.loadUsersState.isLoading = true;
        const usersResponse = await loadUsers(filter);
        this.users = usersResponse.data.data;
        this.lastPage = usersResponse.data.lastPage;
        this.loadUsersState.isLoading = false;
      } catch (error) {
        this.loadUsersState.error = error.response;
        this.loadUsersState.isLoading = false;
      }
    },
    isEmptySearch() {
      return _.isEmpty(this.users) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.users) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      let filter;
      if (event.target.value) {
        filter = { ...this.filter, q: event.target.value };
      } else {
        filter = { ...this.filter };
        delete filter.q;
      }
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
    showStatusColor(user) {
      if (user === 'suspended') {
        return 'warning';
      }
      return 'success';
    },
    getRole(role) {
      return _.get(role, 'name', 'ไม่ระบุ');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/layouts/page.scss';
@import './src/assets/scss/themes/components/form.scss';

.list-item-container {
  .list-item-detail {

    .list-item {
      &.list-item-user-info {
        display: flex;
        flex-direction: row !important;
        justify-content: flex-start !important;
        align-items: center !important;

        .user-image-container {
          margin-right: $spacer;
        }

        .user-detail {
          .title {
            color: $gray-700;
            font-weight: bold;
          }
          .subtitle {
            font-size: $font-size-base * 0.75;
            font-weight: 400;
          }
        }
      }
    }
  }
}


.list-header-container {
  .list-item-container {
    .list-item-detail {

      .list-item {
        &.list-item-user-info {
          .user-detail {
            margin-left: 50px;
          }
        }
      }
    }
  }
}
</style>
